import React from 'react';
import { Button, Row, Col, InputNumber, Select, Card } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useFormContext, useFieldArray, Controller } from 'react-hook-form';
import { useFormStore } from '../../../store/formStore';

export default function ModulosInversoresSection() {
  const { control, watch } = useFormContext();
  const catalogoModulos = useFormStore((s) => s.modulos);
  const catalogoInversores = useFormStore((s) => s.inversores);

  const { fields: moduloFields, append: addModulo, remove: removeModulo } = useFieldArray({ control, name: 'modulos' });
  const { fields: inversorFields, append: addInversor, remove: removeInversor } = useFieldArray({ control, name: 'inversores' });

  const inversores = watch('inversores') || [];
  const totalInversores = inversores.reduce((acc: number, inv: any) => acc + (inv?.quantidade || 0), 0);

  const moduloOptions = catalogoModulos.map((m) => ({
    label: `${m.fabricante} - ${m.modelo} (${m.potencia_wp}Wp)`,
    value: m.id,
  }));

  const inversorOptions = catalogoInversores.map((i) => ({
    label: `${i.fabricante} - ${i.modelo} (${i.potencia_kw}kW)`,
    value: i.id,
  }));

  return (
    <div className="border rounded-lg p-4 mb-4">
      <h3 className="text-lg font-semibold mb-4">Equipamentos</h3>

      {/* Módulos */}
      <h4 className="text-md font-medium mb-2">Módulos Solares</h4>
      {moduloFields.map((field, idx) => (
        <Card key={field.id} size="small" className="mb-2">
          <Row gutter={16} align="middle">
            <Col xs={24} sm={10}>
              <Controller
                name={`modulos.${idx}.catalogo_id`}
                control={control}
                render={({ field: f }) => (
                  <Select
                    {...f}
                    className="w-full"
                    placeholder="Selecione o módulo"
                    options={[...moduloOptions, { label: 'Outro (digitar)', value: -1 }]}
                    allowClear
                  />
                )}
              />
            </Col>
            {watch(`modulos.${idx}.catalogo_id`) === -1 && (
              <Col xs={24} sm={8}>
                <Controller
                  name={`modulos.${idx}.modelo_outro`}
                  control={control}
                  render={({ field: f }) => (
                    <input {...f} value={f.value || ''} className="ant-input w-full" placeholder="Modelo do módulo" />
                  )}
                />
              </Col>
            )}
            <Col xs={12} sm={4}>
              <Controller
                name={`modulos.${idx}.quantidade`}
                control={control}
                render={({ field: f }) => (
                  <InputNumber {...f} className="w-full" min={1} placeholder="Qtd" />
                )}
              />
            </Col>
            <Col xs={12} sm={2}>
              <Button type="text" danger icon={<DeleteOutlined />} onClick={() => removeModulo(idx)} />
            </Col>
          </Row>
        </Card>
      ))}
      <Button type="dashed" icon={<PlusOutlined />} onClick={() => addModulo({ catalogo_id: null, quantidade: 1 })} className="mb-4">
        Adicionar Módulo
      </Button>

      {/* Inversores */}
      <h4 className="text-md font-medium mb-2 mt-4">Inversores</h4>
      {inversorFields.map((field, idx) => (
        <Card key={field.id} size="small" className="mb-2">
          <Row gutter={16} align="middle">
            <Col xs={24} sm={8}>
              <Controller
                name={`inversores.${idx}.catalogo_id`}
                control={control}
                render={({ field: f }) => (
                  <Select
                    {...f}
                    className="w-full"
                    placeholder="Selecione o inversor"
                    options={[...inversorOptions, { label: 'Outro (digitar)', value: -1 }]}
                    allowClear
                    onChange={(val) => {
                      f.onChange(val);
                      if (val && val !== -1) {
                        const inv = catalogoInversores.find((i) => i.id === val);
                        if (inv) {
                          const { setValue } = control._formValues;
                          // This is handled via watch in the form
                        }
                      }
                    }}
                  />
                )}
              />
            </Col>
            {watch(`inversores.${idx}.catalogo_id`) === -1 && (
              <Col xs={24} sm={6}>
                <Controller
                  name={`inversores.${idx}.modelo_outro`}
                  control={control}
                  render={({ field: f }) => (
                    <input {...f} value={f.value || ''} className="ant-input w-full" placeholder="Modelo" />
                  )}
                />
              </Col>
            )}
            <Col xs={8} sm={3}>
              <Controller
                name={`inversores.${idx}.quantidade`}
                control={control}
                render={({ field: f }) => (
                  <InputNumber {...f} className="w-full" min={1} placeholder="Qtd" />
                )}
              />
            </Col>
            <Col xs={8} sm={4}>
              <Controller
                name={`inversores.${idx}.potencia_kw`}
                control={control}
                render={({ field: f }) => (
                  <InputNumber {...f} className="w-full" min={0} step={0.1} placeholder="kW" addonAfter="kW" />
                )}
              />
            </Col>
            <Col xs={8} sm={1}>
              {idx > 0 && <Button type="text" danger icon={<DeleteOutlined />} onClick={() => removeInversor(idx)} />}
            </Col>
          </Row>
        </Card>
      ))}
      {inversorFields.length < 3 && (
        <Button type="dashed" icon={<PlusOutlined />} onClick={() => addInversor({ catalogo_id: null, quantidade: 1, potencia_kw: 0, ordem: inversorFields.length + 1 })}>
          Adicionar Inversor
        </Button>
      )}

      <div className="mt-3 p-2 bg-blue-50 rounded">
        <p className="text-sm font-medium">Total de inversores: {totalInversores}</p>
      </div>
    </div>
  );
}
